import React from 'react';
import { useAuth } from './AuthProvider';
import { getTenantConfig } from '../config/tenantConfig';

// Get tenant config at module load
const tenantConfig = getTenantConfig();

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: tenantConfig.theme.backgroundGradient,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '48px',
    maxWidth: '420px',
    width: '90%',
    textAlign: 'center',
  },
  logo: {
    width: '80px',
    height: '80px',
    background: tenantConfig.logo.gradient,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    fontSize: tenantConfig.logo.text.length > 2 ? '20px' : '32px',
    fontWeight: 'bold',
    color: 'white',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e3a5f',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    marginBottom: '16px',
  },
  headline: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  description: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '32px',
    lineHeight: '1.6',
    maxWidth: '360px',
    margin: '0 auto 32px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    background: `linear-gradient(135deg, ${tenantConfig.theme.primaryColor} 0%, ${tenantConfig.theme.primaryDark} 100%)`,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 14px ${tenantConfig.theme.primaryColor}66`,
  },
  buttonHover: {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px ${tenantConfig.theme.primaryColor}80`,
  },
  microsoftIcon: {
    width: '20px',
    height: '20px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    color: '#94a3b8',
    fontSize: '14px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e2e8f0',
  },
  dividerText: {
    padding: '0 16px',
  },
  info: {
    fontSize: '14px',
    color: '#64748b',
    marginTop: '24px',
  },
  infoHighlight: {
    color: tenantConfig.theme.primaryColor,
    fontWeight: '600',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '24px',
    color: '#dc2626',
    fontSize: '14px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#64748b',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #e2e8f0',
    borderTopColor: tenantConfig.theme.primaryColor,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  poweredBy: {
    fontSize: '13px',
    color: '#94a3b8',
    marginTop: '16px',
    fontWeight: '500',
  },
  poweredByHighlight: {
    color: tenantConfig.theme.primaryColor,
    fontWeight: '600',
  },
  footer: {
    position: 'fixed',
    bottom: '20px',
    right: '24px',
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '500',
  },
};

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

function LoginPage() {
  const { login, loading, error } = useAuth();
  const [isHovered, setIsHovered] = React.useState(false);

  // Set document title based on tenant
  React.useEffect(() => {
    document.title = tenantConfig.name;
  }, []);

  const handleLogin = () => {
    login();
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={styles.card}>
        <div style={styles.logo}>
          {tenantConfig.logo.text}
        </div>
        <h1 style={styles.title}>{tenantConfig.name}</h1>
        <p style={styles.subtitle}>
          {tenantConfig.tagline}
        </p>

        <p style={styles.headline}>
          Decision Intelligence for Spatial & Operational Data
        </p>
        <p style={styles.description}>
          We let business users ask questions directly to maps, land, assets, and operations — and get instant, explainable answers.
        </p>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <span>Signing in...</span>
          </div>
        ) : (
          <button
            style={{
              ...styles.button,
              ...(isHovered ? styles.buttonHover : {}),
            }}
            onClick={handleLogin}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <MicrosoftLogo />
            Sign in with Microsoft
          </button>
        )}

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>Secure login</span>
          <div style={styles.dividerLine}></div>
        </div>

        {tenantConfig.accessRestriction ? (
          <p style={styles.info}>
            Access restricted to <span style={styles.infoHighlight}>{tenantConfig.accessRestriction}</span> accounts only.
            <br />
            Contact IT if you need access.
          </p>
        ) : (
          <p style={styles.info}>
            Demo environment - Sign in with your Microsoft account
          </p>
        )}

        <p style={styles.poweredBy}>
          Powered by <span style={styles.poweredByHighlight}>NeIO 2.0</span>
        </p>
      </div>

      <div style={styles.footer}>
        CODVO AI
      </div>
    </div>
  );
}

export default LoginPage;
